import PageMeta from "components/PageMeta";
import DevicesAndSessions from "components/DevicesAndSessions";
import { GetRouterQuery } from "components/Utils/GetRouterQuery";
import { User } from "models/User";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { Alert, Card, CardBody, Col, Row } from "reactstrap";
import { bindActionCreators } from "redux";
import { getUserById, updateUser } from "store/actions/Users";
import { UsersState } from "store/reducers/Users";
import { UserState } from "store/reducers/User";
import { RootState } from "store/store";
import ManagedUserForm from "./ManagedUserForm";
import { useOfficeTranslation } from "i18n/OfficeTranslation";
import ManagedUserMfaCard from "./ManagedUserMfaCard";

interface Props { usersState: UsersState; canManageMfa: boolean; }
interface Actions { getUserById: (id: string) => Promise<User>; updateUser: (user: User) => Promise<User>; }

export const UserDetail: React.FC<Props & Actions> = ({ usersState, canManageMfa, getUserById, updateUser }) => {
    const { t } = useOfficeTranslation();
    const { id } = GetRouterQuery<{ id: string }>();
    const user = usersState.getUserById(id);
    const [loading, setLoading] = useState(!user);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    useEffect(() => { Promise.resolve(getUserById(id)).finally(() => setLoading(false)); }, [getUserById, id]);
    const save = (changed: User) => {
        setSaving(true); setSaved(false);
        return Promise.resolve(updateUser(changed)).then(() => getUserById(id)).then(result => { setSaved(true); return result; }).finally(() => setSaving(false));
    };
    if (loading && !user) return <div className="page-content text-center text-muted py-5">{t("user.userDetail.loadingUser")}</div>;
    if (!user) return <div className="page-content"><Card><CardBody className="text-center py-5"><h4>{t("user.userDetail.userNotFound")}</h4><Link to="/users">{t("user.userDetail.backToUsers")}</Link></CardBody></Card></div>;
    const initials = user.name?.split(" ").slice(0, 2).map(part => part[0]).join("").toUpperCase();
    const userLevel = Number(user.level);
    const levelName = t(userLevel === 20 ? "user.roles.administrator" : userLevel === 12 ? "user.roles.operator" : userLevel === 5 ? "user.roles.user" : "common.unknown");
    const devices = user.devices ?? [];
    return <div className="page-content"><PageMeta title={user.name} />
        <div className="page-title-box d-flex justify-content-between align-items-center"><div><Link to="/users" className="text-muted"><i className="mdi mdi-arrow-left me-1" />{t("common.users")}</Link><h4 className="mt-2 mb-0">{user.name}</h4></div><span className="badge bg-light text-dark border">{t("user.userDetail.idValue", {id: user.id})}</span></div>
        {saved && <Alert color="success">{t("user.userDetail.userSavedSuccessfully")}</Alert>}
        <Row><Col xl={4}><Card><CardBody className="text-center"><div className="avatar-lg mx-auto mb-3"><span className="avatar-title rounded-circle bg-primary bg-soft text-primary font-size-24">{initials}</span></div><h4>{user.name}</h4><p className="text-muted mb-2">{user.email}</p><span className={`badge ${user.activated ? "bg-success bg-soft text-success" : "bg-danger bg-soft text-danger"}`}>{t(user.activated ? "common.status.active" : "common.status.inactive")}</span><hr /><div className="text-start"><p className="text-muted mb-1">{t("common.accessLevel")}</p><strong>{levelName}</strong><p className="text-muted mt-3 mb-1">{t("common.tenants")}</p><strong>{user.accounts?.length ?? 0}</strong></div></CardBody></Card>
            <ManagedUserMfaCard user={user} canManage={canManageMfa} onConfigured={() => getUserById(id)} /></Col>
            <Col xl={8}><ManagedUserForm key={`${user.id}-${user.email}-${user.activated}-${user.level}`} initial={user} saving={saving} onSave={save} />
            <DevicesAndSessions devices={devices} /></Col></Row>
    </div>;
};

const mapStateToProps = (state: RootState) => ({
    usersState: state.Users,
    canManageMfa: Number((state.User as UserState).getUser()?.level) >= 20,
});
const mapDispatchToProps = (dispatch: any) => bindActionCreators({ getUserById, updateUser }, dispatch);
export default connect(mapStateToProps, mapDispatchToProps)(UserDetail);
